// Note: this hook is for calculation of the new loan, not the existing one.
// When we have a loan amount we can calculate the total amount of the loan like this:

import { useCallback, useEffect, useMemo, useState } from "react"
import { useRefreshPassport } from "@mezo-org/passport"
import { PayloadAction } from "@reduxjs/toolkit"

import {
  borrowMusd,
  claimMatsForBorrow,
  selectBorrowHistory,
  selectClaimableMatsForBorrow,
  selectCollateralAmount,
  selectCollateralPrice,
  selectGasCompensation,
  selectLiquityProps,
  selectLoanAmount,
  selectMinimumCollateralRatioInCurrentMode,
  selectMinimumNetDebt,
  selectTotalMatsForBorrow,
  selectUserHasClosedOrLiquidatedLoan,
  selectUserLoan,
  selectWalletAddress,
  setCollateralAmount,
  setLoanAmount,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  ENABLE_MATS_REWARDS_FOR_BORROW,
  MATS_FOR_BORROW_REWARD_RATIO,
} from "#/shared/constants"
import {
  bigIntToCommaSeparatedFloat,
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  multiplyBigIntWithDecimal,
  normalizeDecimalNumberInput,
  separateThousandsWithComma,
  trimDecimals,
} from "#/shared/utils"

import { useFetchMats } from "../mats"
import {
  usePosthogBorrowEvent,
  usePosthogMatsForBorrowEvent,
} from "../posthog-events"

import {
  useMaxCollateralBalance,
  useMaxLoanAmount,
  useMaxSafeLoanAmount,
  useMinimumRequiredCollateral,
} from "./borrow-calculation-helpers"
import { useBorrowingFee, useFetchAllBorrowData } from "./borrow-system"

// IMPORTANT: this hook should be ONLY used for the "new loan" flows -
// it is calculating ratio with the assumption that the loan amount is the new one
// and the fees are not included in the loanAmount.
// For the existing loan flows, use the useCollateralizationRatioForExistingLoan hook
export const useNewLoanCollateralizationRatio = (
  loanAmount: bigint,
  collateralAmount: bigint,
) => {
  const gasCompensation = useDappSelector(selectGasCompensation)
  const collateralPrice = useDappSelector(selectCollateralPrice)

  const { calculateBorrowingFee } = useBorrowingFee()

  const loanWithFeesValue = useMemo(
    () =>
      gasCompensation && loanAmount
        ? loanAmount + gasCompensation + calculateBorrowingFee(loanAmount)
        : null,
    [loanAmount, gasCompensation, calculateBorrowingFee],
  )

  const collateralValue = useMemo(
    () =>
      collateralPrice && collateralAmount
        ? collateralPrice * collateralAmount
        : null,
    [collateralPrice, collateralAmount],
  )

  const collateralizationRatio = useMemo(
    () =>
      collateralValue && loanWithFeesValue
        ? collateralValue / loanWithFeesValue
        : null,
    [loanWithFeesValue, collateralValue],
  )

  const collateralizationRatioLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat((collateralizationRatio ?? 0n) * 100n),
        0,
      ),
    [collateralizationRatio],
  )

  return {
    collateralizationRatio,
    collateralizationRatioLabel,
    loanWithFeesValue,
  }
}

export const useMinimumRequiredCollateralForNewLoan = () => {
  const minimumLoanAmount = useDappSelector(selectMinimumNetDebt)
  const gasCompensation = useDappSelector(selectGasCompensation)

  const { calculateBorrowingFee } = useBorrowingFee()

  const loanValue = useMemo(
    () =>
      gasCompensation && minimumLoanAmount
        ? minimumLoanAmount +
          gasCompensation +
          calculateBorrowingFee(minimumLoanAmount)
        : 0n,
    [minimumLoanAmount, gasCompensation, calculateBorrowingFee],
  )

  const { requiredCollateralAmount } = useMinimumRequiredCollateral(loanValue)

  return { requiredCollateralAmount }
}

// loanAmountWithFees = loanAmount + gasCompensation + (borrowingFeeRate * loanAmount)
export const useLoanAmountWithFees = () => {
  const loanAmount = useDappSelector(selectLoanAmount)
  const liquityProps = useDappSelector(selectLiquityProps)

  const { gasCompensation = 0n, borrowingFeeRate = 0n } = liquityProps ?? {}

  const issuanceFee = useMemo(
    () =>
      borrowingFeeRate
        ? multiplyBigIntWithDecimal(borrowingFeeRate, Number(loanAmount))
        : 0n,
    [borrowingFeeRate, loanAmount],
  )

  const totalLoanAmount = useMemo(
    () => floatToBigInt(loanAmount) + gasCompensation + issuanceFee,
    [issuanceFee, gasCompensation, loanAmount],
  )

  const totalLoanAmountLabel = bigIntToCommaSeparatedFloat(totalLoanAmount)

  return {
    totalLoanAmount,
    totalLoanAmountLabel,
    gasCompensation,
    issuanceFee,
    borrowingFeeRate,
  }
}

const useBorrowFormLoanAmount = () => {
  const dispatch = useDappDispatch()
  const loanAmount = useDappSelector(selectLoanAmount)

  const { maxEligibleLoanAmount } = useMaxLoanAmount()

  const {
    maxEligibleLoanAmount: maxSafeLoanAmount,
    maxCollateralBalance: maxSafeCollateralBalance,
  } = useMaxSafeLoanAmount()

  const minimumLoanAmount = useDappSelector(selectMinimumNetDebt)

  // User is changing the loan amount input
  const onLoanAmountChange = useCallback(
    (loan: string) => {
      const normalizedLoan = normalizeDecimalNumberInput(loan)
      const trimmedLoan = trimDecimals(normalizedLoan, 2)

      dispatch(setLoanAmount(trimmedLoan))
    },
    [dispatch],
  )

  const onMaxLoanAmount = useCallback(() => {
    if (!maxSafeLoanAmount) return

    const maxLoan = bigIntToFloat(maxSafeLoanAmount, 18, 2)
    const maxCollateral = bigIntToFloat(maxSafeCollateralBalance, 18, 18)
    dispatch(setLoanAmount(maxLoan))
    dispatch(setCollateralAmount(maxCollateral))
  }, [dispatch, maxSafeCollateralBalance, maxSafeLoanAmount])

  const isLoanAmountValid = useMemo(() => {
    const amount = floatToBigInt(loanAmount)

    if (!amount || !maxEligibleLoanAmount || !minimumLoanAmount) return false

    if (amount > maxEligibleLoanAmount) return false

    if (amount < minimumLoanAmount) return false

    return true
  }, [loanAmount, maxEligibleLoanAmount, minimumLoanAmount])

  return {
    loanAmount,
    onMaxLoanAmount,
    onLoanAmountChange,
    isLoanAmountValid,
  }
}

const useBorrowFormCollateralAmount = () => {
  const dispatch = useDappDispatch()
  const collateralAmount = useDappSelector(selectCollateralAmount)
  const loanAmount = useDappSelector(selectLoanAmount)

  const minimumCollateralizationRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const { maxCollateralBalance } = useMaxCollateralBalance()
  const { requiredCollateralAmount } = useMinimumRequiredCollateralForNewLoan()

  const { collateralizationRatio } = useNewLoanCollateralizationRatio(
    floatToBigInt(loanAmount),
    floatToBigInt(collateralAmount),
  )

  // User is changing the collateral amount input
  const onCollateralAmountChange = useCallback(
    (collateral: string) => {
      const normalizedCollateral = normalizeDecimalNumberInput(collateral)

      const trimmedCollateral = trimDecimals(normalizedCollateral, 18)

      dispatch(setCollateralAmount(trimmedCollateral))
    },
    [dispatch],
  )

  const isCollateralAmountValid = useMemo(() => {
    const collateral = floatToBigInt(collateralAmount)

    if (collateral > maxCollateralBalance) return false

    if (collateral < requiredCollateralAmount) return false

    // Don't validate based on ratio if before form is filled in completely
    if (!loanAmount || !collateralAmount) return true

    if (!collateralizationRatio || !minimumCollateralizationRatio) return false

    if (collateralizationRatio < minimumCollateralizationRatio) return false

    return true
  }, [
    collateralAmount,
    collateralizationRatio,
    loanAmount,
    maxCollateralBalance,
    minimumCollateralizationRatio,
    requiredCollateralAmount,
  ])

  return {
    collateralAmount,
    onCollateralAmountChange,
    isCollateralAmountValid,
  }
}

const useBorrowFormRatioAmount = () => {
  const dispatch = useDappDispatch()

  const [ratio, setRatio] = useState("")

  const loanAmount = useDappSelector(selectLoanAmount)
  const collateralAmount = useDappSelector(selectCollateralAmount)
  const collateralPrice = useDappSelector(selectCollateralPrice)

  const minimumCollateralizationRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const { collateralizationRatio, loanWithFeesValue } =
    useNewLoanCollateralizationRatio(
      floatToBigInt(loanAmount),
      floatToBigInt(collateralAmount),
    )

  const setCollateralBasedOnRatio = useCallback(() => {
    if (ratio === "") return

    const ratioValue = floatToBigInt(Number(ratio))

    if (loanWithFeesValue === null || !ratioValue || !collateralPrice) return

    const newCollateral = (loanWithFeesValue * ratioValue) / collateralPrice

    dispatch(setCollateralAmount(bigIntToFloat(newCollateral / 100n, 18, 18))) // ratio is in percentage
  }, [collateralPrice, dispatch, loanWithFeesValue, ratio])

  const onRatioChange = useCallback(
    (newRatio: string) => {
      const ratioNormalizedLabel = normalizeDecimalNumberInput(newRatio)

      const trimmedRatio = trimDecimals(ratioNormalizedLabel, 2)

      setRatio(trimmedRatio)
    },
    [setRatio],
  )

  useEffect(() => {
    if (loanAmount && collateralAmount) {
      setRatio(bigIntToFloat((collateralizationRatio ?? 0n) * 100n, 18, 2))
    }
  }, [collateralAmount, collateralizationRatio, loanAmount])

  const isRatioValid = useMemo(() => {
    // Don't validate if before form is filled in completely
    if (!loanAmount || !collateralAmount) return true

    if (
      !collateralizationRatio ||
      collateralizationRatio <= 0 ||
      !minimumCollateralizationRatio
    )
      return false

    if (collateralizationRatio < minimumCollateralizationRatio) return false

    return true
  }, [
    collateralAmount,
    collateralizationRatio,
    loanAmount,
    minimumCollateralizationRatio,
  ])

  return {
    ratio,
    onRatioChange,
    setCollateralBasedOnRatio,
    isRatioValid,
  }
}

export const useBorrowForm = () => {
  const { loanAmount, onLoanAmountChange, isLoanAmountValid, onMaxLoanAmount } =
    useBorrowFormLoanAmount()

  const {
    collateralAmount,
    onCollateralAmountChange,
    isCollateralAmountValid,
  } = useBorrowFormCollateralAmount()

  const { ratio, onRatioChange, setCollateralBasedOnRatio, isRatioValid } =
    useBorrowFormRatioAmount()

  const isLoanValid = useMemo(
    () => isRatioValid && isCollateralAmountValid && isLoanAmountValid,
    [isCollateralAmountValid, isLoanAmountValid, isRatioValid],
  )

  return {
    ratio,
    loanAmount,
    collateralAmount,
    onLoanAmountChange,
    onMaxLoanAmount,
    onCollateralAmountChange,
    onRatioChange,
    setCollateralBasedOnRatio,
    isLoanValid,
    isRatioValid,
    isLoanAmountValid,
    isCollateralAmountValid,
  }
}

export const useBorrowTransaction = () => {
  const dispatch = useDappDispatch()

  const address = useDappSelector(selectWalletAddress)
  const loanAmount = useDappSelector(selectLoanAmount)
  const collateralAmount = useDappSelector(selectCollateralAmount)

  const { emitBorrowErrorEvent, emitBorrowedEvent } = usePosthogBorrowEvent()
  const { fetchAllBorrowData } = useFetchAllBorrowData()

  const { refreshTokensBalances } = useRefreshPassport()

  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const borrowHandler = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)

    const normalizedLoanAmount = floatToBigInt(loanAmount)

    const normalizedCollateralAmount = floatToBigInt(collateralAmount)

    const { payload: success } = (await dispatch(
      borrowMusd({
        assetAmount: normalizedCollateralAmount,
        debtAmount: normalizedLoanAmount,
      }),
    )) as PayloadAction<boolean>

    if (success) {
      emitBorrowedEvent(address!, loanAmount, collateralAmount)

      await fetchAllBorrowData({ refetch: true })
      await refreshTokensBalances()
    } else {
      emitBorrowErrorEvent(address!, loanAmount, collateralAmount)
    }

    setIsLoading(false)
    setHasError(!success)
  }, [
    loanAmount,
    collateralAmount,
    dispatch,
    emitBorrowedEvent,
    address,
    fetchAllBorrowData,
    refreshTokensBalances,
    emitBorrowErrorEvent,
  ])

  return {
    borrowHandler,
    hasError,
    isLoading,
  }
}

// Calculate mats for borrow based on current loan setup in the form.
// This is NOT showing the current claimable mats amount based on the chain state.
export const useCalculateMatsForBorrow = (amount?: string) => {
  const existingLoanAmount = useDappSelector(selectLoanAmount)
  const loanAmount = amount ?? existingLoanAmount

  const matsForBorrow = useMemo(() => {
    const loanAmountNumber = Number(loanAmount)
    if (Number.isNaN(loanAmountNumber) || loanAmountNumber <= 0) return 0

    return Math.round(loanAmountNumber * MATS_FOR_BORROW_REWARD_RATIO)
  }, [loanAmount])

  const matsForBorrowLabel = useMemo(() => {
    if (matsForBorrow > 10 ** 8) return ">100M"

    if (matsForBorrow > 10 ** 6)
      return `${separateThousandsWithComma(matsForBorrow / 10 ** 6, 0, 0)}M`

    return `${separateThousandsWithComma(matsForBorrow, 0, 0)}`
  }, [matsForBorrow])

  return {
    matsForBorrow,
    matsForBorrowLabel,
  }
}

export const useCanClaimMatsForNextBorrow = () => {
  const borrowHistory = useDappSelector(selectBorrowHistory)
  const hasClosedLoan = useDappSelector(selectUserHasClosedOrLiquidatedLoan)

  // We are only granting mats for the first borrow.
  // User can have unclaimed mats for the first borrow while already taking
  // another loan, so to prevent double claiming we need to check history.
  const canClaimMatsForBorrow = useMemo(
    () =>
      borrowHistory.length === 0 &&
      !hasClosedLoan &&
      ENABLE_MATS_REWARDS_FOR_BORROW,
    [borrowHistory, hasClosedLoan],
  )

  return { canClaimMatsForBorrow }
}

export const useClaimMatsForBorrow = () => {
  const dispatch = useDappDispatch()
  const address = useDappSelector(selectWalletAddress)
  const currentLoan = useDappSelector(selectUserLoan)
  const claimableMats = useDappSelector(selectClaimableMatsForBorrow)
  const totalMats = useDappSelector(selectTotalMatsForBorrow)

  const { emitBorrowMatsClaimedEvent, emitBorrowMatsClaimErrorEvent } =
    usePosthogMatsForBorrowEvent()

  const { fetchMats } = useFetchMats()

  const [isClaiming, setIsClaiming] = useState(false)
  const [hasClaimed, setHasClaimed] = useState(false)

  const claim = useCallback(async () => {
    if (!ENABLE_MATS_REWARDS_FOR_BORROW) return false

    setIsClaiming(true)

    const { payload: result } = await dispatch(claimMatsForBorrow())

    setIsClaiming(false)

    const debtAmount = bigIntToFloat(currentLoan?.debtAmount ?? 0n, 18, 4)

    const collateralAmount = bigIntToFloat(
      currentLoan?.collateralAmount ?? 0n,
      18,
      4,
    )

    if (result) {
      emitBorrowMatsClaimedEvent(address!, claimableMats, debtAmount)
      setHasClaimed(true)

      await fetchMats()
    } else {
      emitBorrowMatsClaimErrorEvent(address!, debtAmount, collateralAmount)
    }

    return result
  }, [
    address,
    claimableMats,
    currentLoan?.collateralAmount,
    currentLoan?.debtAmount,
    dispatch,
    emitBorrowMatsClaimErrorEvent,
    emitBorrowMatsClaimedEvent,
    fetchMats,
  ])

  return {
    totalMats,
    claim,
    claimableMats,
    isClaiming,
    hasClaimed,
  }
}
